import type {AuthState, AuthUser} from '@types';
import {Storage} from './storage';

const AUTH_STORAGE_KEY = 'auth';

export const REFRESH_TOKEN_EXPIRATION_BUFFER_MS = 30_000;

type AuthStateInput = {
    token?: string | null;
    refresh_token?: string | null;
    refresh_token_expiration?: number | string | null;
    user?: AuthUser | null;
};

const normalizeRefreshTokenExpiration = (
    expiration: number | string | null | undefined
): number | null => {
    if (expiration === null || expiration === undefined) {
        return null;
    }

    const expirationNumber = Number(expiration);
    if (!Number.isFinite(expirationNumber) || expirationNumber <= 0) {
        return null;
    }

    return expirationNumber;
};

export const getRefreshTokenExpirationMs = (
    expiration: number | string | null | undefined
): number | null => {
    const expirationNumber = normalizeRefreshTokenExpiration(expiration);

    return expirationNumber === null ? null : expirationNumber * 1000;
};

export const createAuthState = (auth: AuthStateInput): AuthState | null => {
    if (!auth.token || !auth.refresh_token || !auth.user) {
        return null;
    }

    return {
        token: auth.token,
        refresh_token: auth.refresh_token,
        refresh_token_expiration: normalizeRefreshTokenExpiration(
            auth.refresh_token_expiration
        ),
        user: auth.user,
    };
};

export const isRefreshTokenExpired = (
    auth: Pick<
        AuthState,
        'token' | 'refresh_token' | 'refresh_token_expiration'
    >,
    now = Date.now()
): boolean => {
    if (!auth.token || !auth.refresh_token) {
        return false;
    }

    const expirationMs = getRefreshTokenExpirationMs(
        auth.refresh_token_expiration
    );

    return (
        expirationMs !== null &&
        expirationMs <= now + REFRESH_TOKEN_EXPIRATION_BUFFER_MS
    );
};

export const getStoredAuthState = (): AuthState | null => {
    const auth = createAuthState(
        Storage.getObjData<AuthStateInput>(AUTH_STORAGE_KEY) ?? {}
    );

    if (auth === null || isRefreshTokenExpired(auth)) {
        Storage.removeData(AUTH_STORAGE_KEY);
        return null;
    }

    return auth;
};
